import { useMemo } from 'react';
import { AgxSelect, AgxMultiSelect, AgxColumn, AgxLabel, AgxRow } from '@urbanx/agx-ui-components';
import { isEqual } from 'lodash-es';
import { IPermission } from 'types/agency';
import { AgencyAgentModel } from '@urbanx/agx.agencies.client';

interface IAgentsSelectorProps {
  agents: AgencyAgentModel[];
  permission: IPermission;
  setPermission: (
    updatePermission: (previousState: IPermission | undefined) => IPermission
  ) => void;
}

const createOptions = (
  selectedAgentId: string,
  agentOptions: { value: string; label: string }[]
) => {
  return [
    {
      value: '',
      label: 'Please select an agent',
    },
  ].concat(agentOptions.filter((agent) => agent.value !== selectedAgentId));
};

const AgentsSelector = ({ agents, permission, setPermission }: IAgentsSelectorProps) => {
  const agentOptions = useMemo(
    () =>
      agents.map((agent) => {
        return {
          value: agent.id!,
          label: `${agent.name?.firstName} ${agent.name?.lastName}`,
        };
      }),
    [agents]
  );

  const leadAgentOptions = useMemo(
    () => createOptions(permission.secondAgentId!, agentOptions),
    [agentOptions, permission.secondAgentId]
  );

  const secondAgentOptions = useMemo(
    () => createOptions(permission.leadAgentId!, agentOptions),
    [agentOptions, permission.leadAgentId]
  );

  const leadAgentDefaultValue = useMemo(() => {
    return leadAgentOptions.find((agent) => agent.value === permission.leadAgentId);
  }, [leadAgentOptions, permission.leadAgentId]);

  const secondAgentDefaultValue = useMemo(() => {
    return secondAgentOptions.find((agent) => agent.value === permission.secondAgentId);
  }, [permission.secondAgentId, secondAgentOptions]);

  const createdByUser = useMemo(
    () => agents.find((agent) => agent.id === permission.createdByUserId),
    [agents, permission.createdByUserId]
  );

  if (!permission) return null;

  return (
    <AgxColumn extraLargeGap extraClasses={'panelTitleContainer'}>
      <AgxRow>
        <AgxLabel medium>Created By:&nbsp;&nbsp;</AgxLabel>
        <AgxLabel medium>{`${createdByUser?.name?.firstName ?? ''} ${
          createdByUser?.name?.lastName ?? ''
        }`}</AgxLabel>
      </AgxRow>
      <AgxSelect
        id={`leadAgent`}
        label="Lead Agent"
        options={leadAgentOptions}
        onValueChanged={({ value }) =>
          setPermission((previousState) => ({
            ...previousState,
            leadAgentId: value,
          }))
        }
        required
        defaultValue={leadAgentDefaultValue}
      />
      <AgxSelect
        id={`secondAgent`}
        label="Second Agent"
        options={secondAgentOptions}
        onValueChanged={({ value }) =>
          setPermission((previousState) => ({
            ...previousState,
            secondAgentId: value,
          }))
        }
        defaultValue={secondAgentDefaultValue}
      />

      <AgxMultiSelect
        id={`userAccess`}
        label="Additional User Access"
        hideOptionalLabel
        defaultValue={permission.assignedAgentIds ?? []}
        onValueChanged={({ value }: { value: string[] }) => {
          if (!isEqual(value, permission.assignedAgentIds)) {
            setPermission((previousState) => ({
              ...previousState,
              assignedAgentIds: value,
            }));
          }
        }}
        options={agentOptions}
      />
    </AgxColumn>
  );
};

export default AgentsSelector;
